import Divider from '@mui/material/Divider';
import { FormProvider, useForm } from 'react-hook-form';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import LoadingButton from '@mui/lab/LoadingButton';
import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import {
  BaseDrawer,
  BaseDrawerTitle,
} from '@mgm/common/components/base';
import { PasswordField } from '../../../components/form';
import { useAppDispatch, useAppSelector } from '../../../hooks/useAppStore';
import { getIsOpenIntegrationNav } from '../../../store/settings/selectors';
import { settingsActions } from '../../../store/settings/slice';
import { useEditDorIntegration } from '../hooks/useEditIntegration';
import { useDorIntegrationAPI } from '../../../hooks/api/useIntegrations';
import { commonActions } from '../../../store/common/slice';

const IntegrationNav = () => {
  const isOpenIntegrationNav = useAppSelector(getIsOpenIntegrationNav);
  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);
  const dispatch = useAppDispatch();
  const { isEdited, setFormInputs, formInputs } = useEditDorIntegration();
  const { mutateAsync, isLoading } = useDorIntegrationAPI();
  const { t } = useTranslation('settings');

  const methods = useForm({
    mode: 'all',
    values: {
      apiKey: formInputs.apiKey,
      teamId: formInputs.teamId,
    },
  });

  const handleCloseNav = () => {
    dispatch(settingsActions.setIsOpenIntegrationNav(false));
  };

  const handleFormChange = (key: string, value: unknown) => {
    setFormInputs((prevState) => ({
      ...prevState, [key]: value,
    }));
  };

  const connectToIntegration = async () => {
    const data = {
      teamId: formInputs.teamId?.trim(),
      apiKey: formInputs.apiKey?.trim(),
    };
    await mutateAsync(data);
  };

  const onCloseDrawer = () => {
    dispatch(commonActions.setToggleDrawer(false));
  };

  return (
    <BaseDrawer
      drawerProps={{
        anchor: 'right',
        width: isMobile ? 'auto' : 600,
        variant: 'persistent',
      }}
      onCloseDrawer={onCloseDrawer}
      open={isOpenIntegrationNav}
    >
      <BaseDrawerTitle handleClose={handleCloseNav} title={t('integrateWithDor')} />
      <Divider sx={{ borderStyle: 'dashed' }} />
      <FormProvider {...methods}>
        <Box
          paddingRight={isMobile ? 2 : 5}
          paddingLeft={isMobile ? 2 : 5}
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <Grid paddingTop={2} container spacing={4}>
            <Grid size={{
              xs: 12, sm: 12, md: 12, lg: 12,
            }}
            >
              <PasswordField
                placeholder={t('enterTeamId')}
                labelText={t('teamId')}
                name="teamId"
                onChange={(teamId) => handleFormChange('teamId', teamId)}
                isRequired
              />
            </Grid>
            <Grid size={{
              xs: 12, sm: 12, md: 12, lg: 12,
            }}
            >
              <PasswordField
                placeholder={t('enterApiKey')}
                labelText={t('apiKey')}
                name="apiKey"
                onChange={(apiKey) => handleFormChange('apiKey', apiKey)}
                isRequired
              />
            </Grid>
            <Grid
              size={{
                xs: 12, lg: 12,
              }}
              paddingTop={4}
            >
              <LoadingButton
                onClick={methods.handleSubmit(connectToIntegration)}
                loading={isLoading}
                disabled={!methods.formState.isValid || !isEdited}
                size="large"
                variant="contained"
              >
                {t('integrate')}
              </LoadingButton>
            </Grid>
          </Grid>
        </Box>
      </FormProvider>
    </BaseDrawer>
  );
};

export {
  IntegrationNav,
};
